import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { motion } from '@lunary/ui-tokens'

export type PillGeometry = { left: number; right: number; center?: boolean }

type Options = {
  base: string
  squash: number | null
  measure: (container: HTMLElement, item: HTMLElement) => PillGeometry
  onPlace?: (pill: HTMLElement, geometry: PillGeometry) => void
}

export function useLiquidPill<T extends HTMLElement>(activeKey: string | null, { base, squash, measure, onPlace }: Options) {
  const pillRef = useRef<T>(null)
  const items = useRef<Record<string, HTMLElement | null>>({})
  const activeRef = useRef(activeKey)
  const prevLeft = useRef<number | null>(null)
  const timer = useRef<number | undefined>(undefined)

  const register = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      items.current[key] = el
    },
    [],
  )

  const place = useCallback(
    (key: string | null, instant: boolean) => {
      const pill = pillRef.current
      const container = pill?.parentElement
      if (!pill || !container) return
      const item = key ? items.current[key] : null
      if (!item) {
        pill.style.opacity = '0'
        prevLeft.current = null
        return
      }
      const geometry = measure(container, item)
      const { left, right } = geometry
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const from = prevLeft.current
      pill.style.opacity = '1'
      if (instant || reduce || from === null) {
        pill.style.transition = 'none'
        pill.style.left = `${left}px`
        pill.style.right = `${right}px`
        void pill.offsetWidth
        pill.style.transition = base
      } else if (from !== left) {
        const dist = Math.min(Math.abs(left - from), motion.navLeadMaxDistance)
        const lead = motion.navLeadBase + dist * motion.navLeadPerPx
        const trail = lead + motion.navTrailExtra
        const delay = motion.navTrailDelay
        const front = left > from ? 'right' : 'left'
        const back = front === 'right' ? 'left' : 'right'
        pill.style.transition = `${front} ${lead}s ${motion.navEase}, ${back} ${trail}s ${motion.navEase} ${delay}s, ${base}`
        if (squash !== null) {
          pill.style.top = pill.style.bottom = `${squash + motion.navSquash}px`
          window.clearTimeout(timer.current)
          timer.current = window.setTimeout(() => {
            pill.style.top = pill.style.bottom = ''
          }, (trail + delay) * 600)
        }
        pill.style.left = `${left}px`
        pill.style.right = `${right}px`
      }
      onPlace?.(pill, geometry)
      prevLeft.current = left
    },
    [base, squash, measure, onPlace],
  )

  useLayoutEffect(() => {
    activeRef.current = activeKey
    place(activeKey, false)
  }, [activeKey, place])

  useEffect(() => {
    const replace = () => place(activeRef.current, true)
    window.addEventListener('resize', replace)
    void document.fonts?.ready.then(replace)
    return () => {
      window.removeEventListener('resize', replace)
      window.clearTimeout(timer.current)
    }
  }, [place])

  const itemOf = useCallback((key: string) => items.current[key] ?? null, [])

  return { pillRef, register, itemOf }
}
